import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2, Circle } from 'lucide-react';
import { WeekTimeGrid } from './WeekTimeGrid';

export const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
export const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
export const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const mondayOf = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
export const dayLabel = (iso) => { const d = new Date(iso + 'T00:00:00'); return `${d.getDate()} de ${MONTHS[d.getMonth()]}`; };

const rangeFor = (view, anchor) => {
  if (view === 'day') { const iso = localISO(anchor); return { from: iso, to: iso }; }
  if (view === 'week') {
    const mon = mondayOf(anchor); const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: localISO(mon), to: localISO(sun) };
  }
  // mes: incluimos las semanas completas que se pintan
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { from: localISO(first), to: localISO(last) };
};

export const ItemChip = ({ it, cfg, compact = false, onClick, testId }) => {
  const isTask = it.kind === 'task';
  const TaskIcon = it.completada ? CheckCircle2 : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 min-w-0 w-full text-left rounded px-1 py-0.5 hover:bg-muted/70 ${isTask ? 'border border-dashed border-transparent hover:border-border' : ''}`}
      title={`${cfg.label}${it.title ? ' · ' + it.title : ''}`}
      data-testid={testId}
    >
      {isTask ? (
        <TaskIcon className={`w-3 h-3 shrink-0 ${cfg.text}`} />
      ) : (
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      )}
      <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} truncate ${it.completada ? 'line-through text-muted-foreground' : 'text-foreground/85'}`}>
        {it.time ? <b className="font-medium">{it.time} </b> : null}{it.title}
        {it.privado ? <span className="ml-1 text-muted-foreground">🔒</span> : null}
      </span>
    </button>
  );
};

export function CalendarGrid({ items, typeCfg, fallbackType, onRangeChange, onDayClick, onItemClick, onAddAt, loading, headerExtra, renderDayList, weekTimeGrid = false, onSlotClick, onItemMove }) {
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  useEffect(() => { onRangeChange?.(range); }, [range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = useMemo(() => {
    const map = {};
    for (const it of items || []) { if (it.date) (map[it.date] = map[it.date] || []).push(it); }
    return map;
  }, [items]);

  const monthWeeks = useMemo(() => {
    const y = anchor.getFullYear(), m = anchor.getMonth();
    const startOffset = (new Date(y, m, 1).getDay() + 6) % 7;
    const dim = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [anchor]);

  const weekDaysArr = useMemo(() => {
    const mon = mondayOf(anchor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
  }, [anchor]);

  const todayISO = localISO(new Date());
  const shift = (dir) => setAnchor((a) => {
    const d = new Date(a);
    if (view === 'day') d.setDate(d.getDate() + dir);
    else if (view === 'week') d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    return d;
  });

  const headerLabel = useMemo(() => {
    if (view === 'day') return `${anchor.getDate()} de ${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
    if (view === 'week') {
      const mon = mondayOf(anchor); const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return `${mon.getDate()} ${MONTHS[mon.getMonth()].slice(0, 3)} – ${sun.getDate()} ${MONTHS[sun.getMonth()].slice(0, 3)} ${sun.getFullYear()}`;
    }
    return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor]);

  const cfgOf = (it) => typeCfg[it.type] || typeCfg[fallbackType] || Object.values(typeCfg)[0];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold capitalize" data-testid="period-label">{headerLabel}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {headerExtra}
          <div className="flex items-center rounded-lg border p-0.5" data-testid="view-switch">
            {[['month', 'Mes'], ['week', 'Semana'], ['day', 'Día']].map(([v, lbl]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}-btn`}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="today-btn">Hoy</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="next-btn"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {Object.entries(typeCfg).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`legend-${k}`}>
            {c.isTask ? <Circle className={`w-3 h-3 ${c.text}`} /> : <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />}{c.label}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : view === 'month' ? (
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden" data-testid="month-grid">
          {WEEKDAYS.map((w) => <div key={w} className="bg-muted/50 py-2 text-center text-xs font-medium text-muted-foreground">{w}</div>)}
          {monthWeeks.map((row, ri) => row.map((day, ci) => {
            if (!day) return <div key={`${ri}-${ci}`} className="bg-background min-h-[104px]" />;
            const iso = localISO(day);
            const dayItems = byDate[iso] || [];
            const isToday = iso === todayISO;
            return (
              <div key={iso} role="button" tabIndex={0} onClick={() => onDayClick?.(iso)} onKeyDown={(e) => e.key === 'Enter' && onDayClick?.(iso)}
                className={`bg-background min-h-[104px] p-1.5 text-left align-top hover:bg-muted/40 transition-colors flex flex-col gap-1 cursor-pointer ${isToday ? 'ring-2 ring-primary ring-inset' : ''}`}
                data-testid={`day-cell-${iso}`}>
                <span className={`text-xs font-medium ${isToday ? 'text-primary font-bold' : 'text-foreground'}`}>{day.getDate()}</span>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {dayItems.slice(0, 3).map((it, idx) => (
                    <ItemChip key={idx} it={it} cfg={cfgOf(it)} compact
                      onClick={(e) => { e.stopPropagation(); onItemClick?.(it); }}
                      testId={`month-item-${it.type}-${it.id}`} />
                  ))}
                  {dayItems.length > 3 && <span className="text-[10px] text-primary font-medium pl-1">+{dayItems.length - 3} más</span>}
                </div>
              </div>
            );
          }))}
        </div>
      ) : view === 'week' && weekTimeGrid ? (
        <WeekTimeGrid days={weekDaysArr} items={items} cfgOf={cfgOf} onItemClick={onItemClick} onSlotClick={onSlotClick || ((iso) => onAddAt?.(iso))} onItemMove={onItemMove} />
      ) : view === 'day' && weekTimeGrid ? (
        <WeekTimeGrid days={[anchor]} items={items} cfgOf={cfgOf} onItemClick={onItemClick} onSlotClick={onSlotClick || ((iso) => onAddAt?.(iso))} onItemMove={onItemMove} />
      ) : view === 'week' ? (
        <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
          {weekDaysArr.map((day) => {
            const iso = localISO(day);
            const dayItems = byDate[iso] || [];
            const isToday = iso === todayISO;
            return (
              <div key={iso} className={`rounded-lg border min-h-[240px] flex flex-col ${isToday ? 'border-primary' : ''}`} data-testid={`week-col-${iso}`}>
                <button onClick={() => onAddAt?.(iso)} className={`px-2 py-1.5 text-center border-b ${isToday ? 'bg-primary/10' : 'bg-muted/40'}`} title="Añadir este día">
                  <div className="text-[11px] text-muted-foreground">{WEEKDAYS[(day.getDay() + 6) % 7]}</div>
                  <div className={`text-sm font-semibold ${isToday ? 'text-primary' : ''}`}>{day.getDate()}</div>
                </button>
                <div className="p-1.5 flex flex-col gap-1 overflow-y-auto flex-1">
                  {dayItems.length === 0 ? <span className="text-[10px] text-muted-foreground text-center mt-2">—</span>
                    : dayItems.map((it, idx) => (
                      <ItemChip key={idx} it={it} cfg={cfgOf(it)} onClick={() => onItemClick?.(it)} testId={`week-item-${it.type}-${it.id}`} />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2 max-w-2xl" data-testid="day-view">
          {renderDayList ? renderDayList(byDate[localISO(anchor)] || [], localISO(anchor)) : null}
        </div>
      )}
    </div>
  );
}
